import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Tri-state theme toggle: light → dark → system → light. The icon
 * shown reflects the active resolved theme. Stays inert until the
 * client has hydrated, since `next-themes` reads from localStorage
 * which is only safe post-mount.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const next: Record<string, string> = {
    light: "dark",
    dark: "system",
    system: "light",
  };
  const current = (mounted ? theme : "system") ?? "system";
  const Icon = current === "light" ? Sun : current === "dark" ? Moon : Monitor;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`Switch theme (currently ${current})`}
          onClick={() => setTheme(next[current] ?? "system")}
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground",
            className,
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={6} className="text-xs">
        Theme: {current}
      </TooltipContent>
    </Tooltip>
  );
}
